/* eslint-disable react-refresh/only-export-components */
import { Popover } from 'radix-ui';
import { createContext, ReactNode, useContext, useState } from 'react';
import FilterActiveIcon from '../../assets/icons/filter-active.svg?react';
import TableFilterIcon from '../../assets/icons/table-filter.svg?react';
import { cn } from '../../utils/className';

// Context to provide onClose to any descendant component
const FilterPopoverContext = createContext<{ onClose: () => void } | null>(
  null
);

export function useFilterPopover() {
  return useContext(FilterPopoverContext);
}

export interface TableColumnFilterProps {
  /**
   * The filter content to show in the popover
   */
  children: ReactNode;
  /**
   * Whether the filter has an active value
   */
  isActive?: boolean;
  /**
   * Optional class name for the trigger button
   */
  className?: string;
  /**
   * Optional label for accessibility
   */
  ariaLabel?: string;
}

/**
 * A reusable table column filter component that shows a filter icon in the
 * column header. When clicked, it opens a popover containing the filter content.
 *
 * This component is designed to be used across all tables in the application
 * for consistent filtering UX.
 *
 * @example
 * ```tsx
 * <TableColumnFilter isActive={selectedValues.length > 0}>
 *   <CustomerFilter
 *     selectedValues={selectedValues}
 *     onChange={handleChange}
 *   />
 * </TableColumnFilter>
 * ```
 */
export function TableColumnFilter({
  children,
  isActive = false,
  className,
  ariaLabel = 'Filter column',
}: TableColumnFilterProps) {
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className={cn(
            'flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center',
            'rounded transition-colors',
            'hover:bg-surface-focus-support focus-visible:outline-none',
            '',
            isActive ? 'text-focus' : 'text-tertiary',
            className
          )}
          onClick={(e) => e.stopPropagation()}
          aria-label={ariaLabel}
          title={ariaLabel}
        >
          {isActive ? (
            <FilterActiveIcon className='h-5 w-5' />
          ) : (
            <TableFilterIcon className='h-5 w-5' />
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={cn(
            'z-9999 max-w-80 min-w-50 overflow-hidden rounded-lg',
            'border-border-primary bg-surface-primary border shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2',
            'data-[side=top]:slide-in-from-bottom-2'
          )}
          sideOffset={0}
          align='end'
          onClick={(e) => e.stopPropagation()}
        >
          <FilterPopoverContext.Provider value={{ onClose: handleClose }}>
            {children}
          </FilterPopoverContext.Provider>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
